/**
 * "Manage settings" for one plant PC (agent 0.3.0, migration 0218): an org admin picks which of the agent's settings
 * Sahas Forge manages, and their values. Each setting has a "Manage from Sahas Forge" box; only the ticked ones are
 * sent (PUT /v1/ingest/agents/{agent_id}/settings), and the rest stay the PC's own. The agent takes them on its next
 * sync. "Stop managing" sends {} and hands every setting back to the PC. The logic is lib/plant-agent-settings.ts.
 *
 * The call replaces all the settings managed for the PC, so the body starts from the settings the PC has (the agent's
 * managed_settings): what the dialog does not edit (watched folders, file patterns, remote browse) goes back as it was.
 *
 * Agent 0.4.0 (migration 0231): "Allow saving to a pendrive" (not managed, on or off) and "Explore consent" (what an
 * admin asks an explore request may do, and who asked and when). They have no box of their own. The consent applies only
 * once someone at the PC confirms it in the agent's window (Arjun's decision on #1419, 2026-10-14): the group shows where
 * that stands (GET …/consent), its latest changes, and that Sahas staff and the org's own admins can read the documents an explore sends (2026-10-14). Only an
 * org admin gets this dialog (the Plant agents panel is shown to org admins only, and the API answers 403 to anyone else).
 *
 * ManageSettingsView is pure (the tests call it); ManageSettingsSheet holds the state and the call, in a Sheet (focus
 * moves into it on open and back to the button that opened it on close; Escape closes it).
 */
import { useState, type ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/lib/ui/button";
import { Input } from "@/lib/ui/input";
import { NativeSelect } from "@/lib/ui/native-select";
import { RadioCard, RadioCardGroup } from "@/lib/ui/radio-cards";
import { Sheet, SheetHeader } from "@/lib/ui/sheet";
import { useToast } from "@/lib/ui/toast";
import { cn } from "@/lib/ui/cn";
import {
  type IngestAgent,
  type IngestAgentConsent,
  type IngestManagedSettings,
  type IngestPath,
  type IngestUpdatePolicy,
  useIngestAgentConsentQuery,
  usePutIngestAgentSettings,
} from "@/lib/api/ingest-agents";
import {
  CONSENT_FLAGS,
  CONSENT_FLAG_WORDS,
  CONSENT_STAFF_NOTICE,
  MODE_HINTS,
  MODE_WORDS,
  PATH_WORDS,
  POLICY_WORDS,
  consentEventWords,
  consentItemsWords,
  consentStamp,
  consentStatusWords,
  isManaged,
} from "@/lib/plant-agents";
import {
  type ConsentForm,
  LIMITS,
  type ManagedKey,
  type PendriveChoice,
  SETTINGS_CODE_WORDS,
  type SettingKey,
  type SettingsForm,
  type SettingsInvalid,
  canSave,
  consentAgreed,
  formOfSettings,
  keptWords,
  listInWords,
  settingsErrorWords,
  settingsOfForm,
} from "@/lib/plant-agent-settings";

const ERROR_ID = "plant-agent-settings-error";
const FIELD = "flex flex-col gap-1 text-xs text-on-surface-muted";

const POLICY_HINTS: Record<IngestUpdatePolicy, string> = {
  auto: "Installs when nobody is using the agent's window, else as soon as nobody is.",
  notify: "Says an update is ready; someone at the PC installs it.",
  off: "No updates are offered.",
};

/** One setting: its name, its "Manage from Sahas Forge" box, and its controls (off while it is the PC's own). */
function SettingGroup({
  k,
  title,
  hint,
  form,
  onFormChange,
  disabled,
  children,
}: {
  k: ManagedKey;
  title: string;
  hint?: string;
  form: SettingsForm;
  onFormChange: (f: SettingsForm) => void;
  disabled: boolean;
  children: ReactNode;
}) {
  const managed = form.manage[k];
  const titleId = `plant-agent-${k}-title`;
  return (
    <div
      role="group"
      aria-labelledby={titleId}
      data-setting={k}
      className={cn("flex flex-col gap-2 rounded-md border p-3", managed ? "border-brand/50" : "border-border")}
    >
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
        <span id={titleId} className="text-sm font-medium text-on-surface">
          {title}
        </span>
        <label className="flex items-center gap-1.5 text-xs text-on-surface-muted">
          <input
            type="checkbox"
            name={`manage-${k}`}
            checked={managed}
            disabled={disabled}
            onChange={(e) => onFormChange({ ...form, manage: { ...form.manage, [k]: e.target.checked } })}
          />
          Manage from Sahas Forge
        </label>
      </div>
      {hint && <p className="text-xs text-on-surface-muted">{hint}</p>}
      <div className={cn("flex flex-col gap-2", !managed && "opacity-60")}>{children}</div>
    </div>
  );
}

/** A setting with no box of its own: what is chosen in it says whether it is managed. */
function PlainGroup({
  k,
  title,
  hint,
  managed,
  children,
}: {
  k: "pendrive_save" | "explore_consent";
  title: string;
  hint?: string;
  managed: boolean;
  children: ReactNode;
}) {
  const titleId = `plant-agent-${k}-title`;
  return (
    <div
      role="group"
      aria-labelledby={titleId}
      data-setting={k}
      className={cn("flex flex-col gap-2 rounded-md border p-3", managed ? "border-brand/50" : "border-border")}
    >
      <span id={titleId} className="text-sm font-medium text-on-surface">
        {title}
      </span>
      {hint && <p className="text-xs text-on-surface-muted">{hint}</p>}
      {children}
    </div>
  );
}

const STATUS_TONE: Record<string, string> = { success: "text-success", warning: "text-warning", secondary: "text-on-surface-muted" };

/**
 * Where the explore consent stands at the PC (agent 0.4.0): confirmed there (by whom, when) or waiting for it, what
 * applies now, the latest changes, and who can read what an explore sends (Sahas staff and the org's admins). `state` undefined: being read; null: it
 * can't be read (no live API, or the call failed). `asked`: the PC holds an ask now.
 */
function ConsentAtPC({ state, asked }: { state?: IngestAgentConsent | null; asked: boolean }) {
  const status = consentStatusWords(state);
  return (
    <div className="flex flex-col gap-1.5 rounded-md border border-border p-2 text-xs" data-consent-pc>
      <span className="font-medium text-on-surface">At the PC</span>
      {state === undefined ? (
        <p className="text-on-surface-muted">Reading what the PC confirmed…</p>
      ) : state === null && asked ? (
        <p className="text-on-surface-muted" data-consent-status>
          What the PC confirmed can&apos;t be read now. Only what someone at the PC confirmed applies.
        </p>
      ) : !status ? (
        <p className="text-on-surface-muted" data-consent-status>
          Nothing is asked: an explore runs only the basic inventory on this PC.
        </p>
      ) : (
        <>
          <p className={STATUS_TONE[status.tone]} data-consent-status>
            {status.text}
          </p>
          <p className="text-on-surface-muted" data-consent-effective>
            Applies now: {consentItemsWords(state?.effective)}.
          </p>
        </>
      )}
      {state && state.history.length > 0 && (
        <div className="flex flex-col gap-0.5">
          <span id="plant-agent-consent-history" className="text-on-surface-muted">
            Latest changes
          </span>
          <ol aria-labelledby="plant-agent-consent-history" className="flex flex-col gap-0.5 text-on-surface" data-consent-history>
            {state.history.map((e) => (
              <li key={e.id}>{consentEventWords(e)}</li>
            ))}
          </ol>
        </div>
      )}
      <p className="text-on-surface" data-consent-notice>
        {CONSENT_STAFF_NOTICE} The agent tells the person at the PC so when it asks them to confirm.
      </p>
    </div>
  );
}

/** Saving to a pendrive, as the three cards say it. */
const PENDRIVE_CARDS: { value: PendriveChoice; title: string; description: string }[] = [
  { value: "default", title: "Not managed", description: "On in probe mode, off in operational mode." },
  { value: "on", title: "On", description: "The button works in either mode." },
  { value: "off", title: "Off", description: "The button does not work." },
];

export interface ManageSettingsViewProps {
  agent: IngestAgent;
  form: SettingsForm;
  onFormChange: (f: SettingsForm) => void;
  /** Sends these settings: the managed ones the dialog does not edit, as they stand, and the ones it sets; or {} to stop managing. */
  onSave: (settings: IngestManagedSettings) => void;
  /** A check failed before sending: the API's code for it, the setting it is in, words for this exact problem, the folder it is in. */
  onInvalid: (problem: SettingsInvalid) => void;
  saving: boolean;
  /** The last problem, in words, the setting it is in when known, and the consent's folder (a row, from 0) when it is in one. */
  error: { text: string; key: SettingKey | null; row?: number } | null;
  onClose: () => void;
  /** Agent 0.4.0: the explore consent as it stands at the PC (GET …/consent); undefined while it is read. */
  consent?: IngestAgentConsent | null;
}

/** The dialog's body, pure: exported for the tests. */
export function ManageSettingsView(p: ManageSettingsViewProps) {
  const f = p.form;
  const set = (patch: Partial<SettingsForm>) => p.onFormChange({ ...f, ...patch });
  const off = (k: ManagedKey) => p.saving || !f.manage[k];
  // A problem in one of the consent's folders marks that folder only; any other marks every field of its setting.
  const invalid = (k: SettingKey, row?: number) =>
    p.error?.key === k && (p.error.row === undefined || p.error.row === row)
      ? { "aria-invalid": true as const, "aria-describedby": ERROR_ID }
      : {};
  const group = { form: f, onFormChange: p.onFormChange, disabled: p.saving };
  const managedNow = isManaged(p.agent);
  // Settings the PC has that this dialog does not show: a save sends them back as they were (keptSettings).
  const kept = keptWords(p.agent.managed_settings);
  const consent = f.consent;
  const setConsent = (patch: Partial<ConsentForm>) => set({ consent: { ...consent, ...patch } });
  const given = consentStamp(p.agent.managed_settings?.explore_consent);

  return (
    <form
      className="flex flex-1 flex-col gap-4 p-5"
      aria-label="Managed settings"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        const made = settingsOfForm(f, p.agent.managed_settings);
        if ("error" in made) p.onInvalid(made);
        else p.onSave(made.settings);
      }}
    >
      <p className="text-sm text-on-surface-muted">
        Tick the settings Sahas Forge should manage for this PC. It takes them on its next sync; the settings left
        unticked stay the PC&apos;s own.
      </p>
      {kept.length > 0 && (
        <p className="text-sm text-on-surface-muted" data-kept>
          Not shown here, and kept as they are: {kept.join(", ")}.
        </p>
      )}

      <div role="group" aria-labelledby="plant-agent-control-title" className="flex flex-col gap-2">
        <span id="plant-agent-control-title" className="text-sm font-medium text-on-surface">
          Control
        </span>
        <RadioCardGroup
          name="control"
          value={f.control}
          onValueChange={(v) => set({ control: v as SettingsForm["control"] })}
          disabled={p.saving}
          aria-label="Control"
        >
          <RadioCard
            value="remote"
            title="Locked on the PC (remotely managed)"
            description="Only an admin here can change them."
          />
          <RadioCard
            value="local"
            title="PC can change them"
            description="Applied on the PC when they change here; after that, someone at the PC may change them."
          />
        </RadioCardGroup>
        {kept.length > 0 && (
          <p className="text-xs text-on-surface-muted">
            This also covers the {listInWords(kept)} managed for this PC outside this dialog. A save keeps them as they
            are, and Stop managing ends them too.
          </p>
        )}
      </div>

      <SettingGroup k="mode" title="Mode" {...group}>
        <RadioCardGroup
          name="mode"
          value={f.mode}
          onValueChange={(v) => set({ mode: v as SettingsForm["mode"] })}
          disabled={off("mode")}
          aria-label="Mode"
        >
          <RadioCard value="probe" title={MODE_WORDS.probe} description={MODE_HINTS.probe} />
          <RadioCard value="operational" title={MODE_WORDS.operational} description={MODE_HINTS.operational} />
        </RadioCardGroup>
      </SettingGroup>

      <SettingGroup
        k="samples_per_kind"
        title="Samples per kind"
        hint="In probe mode, how many of the newest files of each kind go first."
        {...group}
      >
        <div className={FIELD}>
          <label htmlFor="plant-agent-samples">Files of each kind ({LIMITS.samples.join("–")})</label>
          <Input
            id="plant-agent-samples"
            type="number"
            inputMode="numeric"
            min={LIMITS.samples[0]}
            max={LIMITS.samples[1]}
            step={1}
            className="w-28"
            value={f.samples}
            onChange={(e) => set({ samples: e.target.value })}
            disabled={off("samples_per_kind")}
            {...invalid("samples_per_kind")}
          />
        </div>
      </SettingGroup>

      <SettingGroup k="drop" title="Drag and drop" {...group}>
        <div className={FIELD}>
          <label htmlFor="plant-agent-drop">Files may be dropped on the agent&apos;s window</label>
          <NativeSelect
            id="plant-agent-drop"
            className="w-28"
            value={f.drop ? "on" : "off"}
            onChange={(e) => set({ drop: e.target.value === "on" })}
            disabled={off("drop")}
          >
            <option value="on">On</option>
            <option value="off">Off</option>
          </NativeSelect>
        </div>
      </SettingGroup>

      <SettingGroup k="bandwidth" title="Bandwidth" hint="A share of the PC's measured upload speed." {...group}>
        {(
          [
            ["catchUpPct", "plant-agent-catch-up", "While catching up", "On the files that were there before the agent was set up."],
            ["afterPct", "plant-agent-after", "Afterwards", "On new files."],
          ] as const
        ).map(([field, id, label, hint]) => (
          <div key={field} className={FIELD}>
            <div className="flex items-center justify-between gap-2">
              <label htmlFor={id}>{label}</label>
              <output htmlFor={id} className="font-mono text-on-surface">
                {f[field]}%
              </output>
            </div>
            <input
              id={id}
              type="range"
              min={LIMITS.bandwidth[0]}
              max={LIMITS.bandwidth[1]}
              step={1}
              value={f[field]}
              aria-valuetext={`${f[field]}% of the measured speed`}
              aria-describedby={`${id}-hint`}
              className="w-full accent-brand"
              onChange={(e) => {
                const n = Number(e.target.value);
                set(field === "catchUpPct" ? { catchUpPct: n } : { afterPct: n });
              }}
              disabled={off("bandwidth")}
            />
            <span id={`${id}-hint`}>{hint}</span>
          </div>
        ))}
      </SettingGroup>

      <SettingGroup k="updates" title="Updates" {...group}>
        <div className={FIELD}>
          <label htmlFor="plant-agent-policy">Policy</label>
          <NativeSelect
            id="plant-agent-policy"
            className="w-40"
            value={f.policy}
            onChange={(e) => set({ policy: e.target.value as IngestUpdatePolicy })}
            disabled={off("updates")}
            aria-describedby="plant-agent-policy-hint"
          >
            {(Object.keys(POLICY_WORDS) as IngestUpdatePolicy[]).map((v) => (
              <option key={v} value={v}>
                {POLICY_WORDS[v]}
              </option>
            ))}
          </NativeSelect>
          <span id="plant-agent-policy-hint">{POLICY_HINTS[f.policy]}</span>
        </div>
        <div className={FIELD}>
          <label htmlFor="plant-agent-pin">Pin to version (optional)</label>
          <Input
            id="plant-agent-pin"
            className="w-40 font-mono"
            placeholder="0.3.1"
            autoComplete="off"
            value={f.pin}
            onChange={(e) => set({ pin: e.target.value })}
            disabled={off("updates")}
            {...invalid("updates")}
          />
          <span>Holds the PC at this version, older or newer: also how to roll an update back.</span>
        </div>
        <label className="flex items-start gap-1.5 text-xs text-on-surface">
          <input
            type="checkbox"
            name="urgent"
            className="mt-0.5"
            checked={f.urgent}
            onChange={(e) => set({ urgent: e.target.checked })}
            disabled={off("updates")}
          />
          <span>
            Force updates on this PC (urgent)
            <span className="block text-on-surface-muted">
              Every update offered to it installs at once; someone using the agent&apos;s window is asked to update
              first.
            </span>
          </span>
        </label>
      </SettingGroup>

      <SettingGroup k="path" title="Route" hint="How the agent reaches Sahas Forge." {...group}>
        <div className={FIELD}>
          <label htmlFor="plant-agent-path">Send</label>
          <NativeSelect
            id="plant-agent-path"
            className="w-56"
            value={f.path}
            onChange={(e) => set({ path: e.target.value as IngestPath })}
            disabled={off("path")}
          >
            {(Object.keys(PATH_WORDS) as IngestPath[]).map((v) => (
              <option key={v} value={v}>
                {PATH_WORDS[v]}
              </option>
            ))}
          </NativeSelect>
        </div>
      </SettingGroup>

      <SettingGroup k="interval_minutes" title="Interval" hint="Minutes between passes." {...group}>
        <div className={FIELD}>
          <label htmlFor="plant-agent-interval">Minutes ({LIMITS.interval.join("–")})</label>
          <Input
            id="plant-agent-interval"
            type="number"
            inputMode="numeric"
            min={LIMITS.interval[0]}
            max={LIMITS.interval[1]}
            step={1}
            className="w-28"
            value={f.interval}
            onChange={(e) => set({ interval: e.target.value })}
            disabled={off("interval_minutes")}
            {...invalid("interval_minutes")}
          />
        </div>
      </SettingGroup>

      <SettingGroup k="max_per_pass" title="Files per pass" {...group}>
        <div className={FIELD}>
          <label htmlFor="plant-agent-per-pass">Files ({LIMITS.perPass.join("–")})</label>
          <Input
            id="plant-agent-per-pass"
            type="number"
            inputMode="numeric"
            min={LIMITS.perPass[0]}
            max={LIMITS.perPass[1]}
            step={1}
            className="w-28"
            value={f.perPass}
            onChange={(e) => set({ perPass: e.target.value })}
            disabled={off("max_per_pass")}
            {...invalid("max_per_pass")}
          />
        </div>
      </SettingGroup>

      <PlainGroup
        k="pendrive_save"
        title="Allow saving to a pendrive"
        hint="Whether the Save to a pendrive button in the agent's Check this PC works. Only Sahas Forge opens the file it saves. The person at the PC has no switch for it, so it applies with either control."
        managed={f.pendrive !== "default"}
      >
        <RadioCardGroup
          name="pendrive"
          value={f.pendrive}
          onValueChange={(v) => set({ pendrive: v as PendriveChoice })}
          disabled={p.saving}
          aria-label="Allow saving to a pendrive"
        >
          {PENDRIVE_CARDS.map((c) => (
            <RadioCard key={c.value} value={c.value} title={c.title} description={c.description} />
          ))}
        </RadioCardGroup>
      </PlainGroup>

      <PlainGroup
        k="explore_consent"
        title="Explore consent"
        hint="What you ask an explore request may do on this PC. It applies only once someone at the PC confirms it in the agent's window. The basic inventory always runs, with no consent. Leave everything empty to ask for nothing. Sahas Forge records who asked and who confirmed, and when."
        managed={consentAgreed(consent)}
      >
        <div className="flex flex-col gap-2">
          {CONSENT_FLAGS.map((k) => (
            <label key={k} className="flex items-start gap-1.5 text-xs text-on-surface">
              <input
                type="checkbox"
                name={`consent-${k}`}
                className="mt-0.5"
                checked={consent[k]}
                onChange={(e) => setConsent({ [k]: e.target.checked })}
                disabled={p.saving}
              />
              <span>
                {CONSENT_FLAG_WORDS[k].label}
                <span className="block text-on-surface-muted">{CONSENT_FLAG_WORDS[k].hint}</span>
              </span>
            </label>
          ))}
        </div>
        <div role="group" aria-labelledby="plant-agent-consent-roots-title" className={FIELD}>
          <span id="plant-agent-consent-roots-title" className="font-medium text-on-surface">
            Folders a deep scan may cover
          </span>
          <span id="plant-agent-consent-roots-hint">
            Any folder below one is covered too. Up to {LIMITS.roots}. Write a full drive path, like D:\PLANT\DATA.
          </span>
          {consent.roots.map((root, i) => (
            <div key={i} className="flex items-center gap-2">
              <Input
                id={`plant-agent-consent-root-${i}`}
                aria-label={`Folder ${i + 1}`}
                aria-describedby="plant-agent-consent-roots-hint"
                className="font-mono"
                placeholder="D:\PLANT\DATA"
                autoComplete="off"
                spellCheck={false}
                value={root}
                onChange={(e) => setConsent({ roots: consent.roots.map((r, j) => (j === i ? e.target.value : r)) })}
                disabled={p.saving}
                {...invalid("explore_consent", i)}
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                aria-label={`Remove folder ${i + 1}`}
                disabled={p.saving}
                onClick={() => setConsent({ roots: consent.roots.filter((_, j) => j !== i) })}
              >
                Remove
              </Button>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="w-fit"
            disabled={p.saving || consent.roots.length >= LIMITS.roots}
            onClick={() => setConsent({ roots: [...consent.roots, ""] })}
          >
            Add folder
          </Button>
        </div>
        {given && (
          <p className="text-xs text-on-surface-muted" data-consent-given>
            Consent {given}.
          </p>
        )}
        <ConsentAtPC state={p.consent} asked={!!p.agent.managed_settings?.explore_consent} />
      </PlainGroup>

      {p.error && (
        <p id={ERROR_ID} role="alert" className="text-sm text-danger">
          {p.error.text}
        </p>
      )}

      <div className="mt-auto flex flex-wrap items-center justify-between gap-2 border-t border-border pt-4">
        <div>
          {managedNow && (
            <Button type="button" variant="outline" size="sm" disabled={p.saving} onClick={() => p.onSave({})}>
              Stop managing
            </Button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button type="button" variant="ghost" size="sm" onClick={p.onClose} disabled={p.saving}>
            Cancel
          </Button>
          <Button type="submit" size="sm" disabled={p.saving || !canSave(f, p.agent)}>
            {p.saving && <Loader2 className="size-4 animate-spin" />}
            Save settings
          </Button>
        </div>
      </div>
    </form>
  );
}

/** The dialog's state and its call: the form starts from what is managed now; a save closes it. */
function ManageSettingsEditor({ agent, onClose }: { agent: IngestAgent; onClose: () => void }) {
  const put = usePutIngestAgentSettings();
  const toast = useToast();
  const [form, setForm] = useState<SettingsForm>(() => formOfSettings(agent.managed_settings));
  const [error, setError] = useState<ManageSettingsViewProps["error"]>(null);
  // Agent 0.4.0: where the explore consent stands at the PC. Null when it can't be read (no live API, or a failure).
  const consentQ = useIngestAgentConsentQuery(agent.id, true);
  const consentState = consentQ.data ?? (consentQ.isError || consentQ.fetchStatus === "idle" ? null : undefined);

  const save = async (settings: IngestManagedSettings) => {
    setError(null);
    try {
      await put.mutateAsync({ id: agent.id, settings });
      toast.push(
        "ok",
        Object.keys(settings).length > 0
          ? `Settings saved for ${agent.name}: the PC takes them on its next sync.`
          : `${agent.name}'s settings are the PC's own again from its next sync.`,
      );
      onClose();
    } catch (err) {
      setError({ text: settingsErrorWords(err), key: null });
    }
  };

  return (
    <ManageSettingsView
      agent={agent}
      form={form}
      onFormChange={(next) => {
        setForm(next);
        setError(null);
      }}
      onSave={(s) => void save(s)}
      onInvalid={(problem) =>
        setError({
          text: problem.detail ?? SETTINGS_CODE_WORDS[problem.error] ?? problem.error,
          key: problem.key,
          row: problem.row,
        })
      }
      saving={put.isPending}
      error={error}
      onClose={onClose}
      consent={consentState}
    />
  );
}

/** The "Manage settings" dialog for `agent`, or nothing. */
export function ManageSettingsSheet({ agent, onClose }: { agent: IngestAgent | null; onClose: () => void }) {
  if (!agent) return null;
  return (
    <Sheet open onClose={onClose} widthClass="max-w-lg" ariaLabel={`Manage settings of ${agent.name}`}>
      <SheetHeader title="Manage settings" subtitle={`${agent.name} · ${agent.identity_id}`} onClose={onClose} />
      <ManageSettingsEditor key={agent.id} agent={agent} onClose={onClose} />
    </Sheet>
  );
}
